<template>
  <app-collapse-item class="rounded" :title="title">
    <validation-observer
      v-for="(contact, index) in contactMethods"
      :key="contact.name_ar"
      ref="updateContactForm"
      #default="{ invalid }"
    >
      <b-form ref="form" :style="{ height: trHeight }" class="repeater-form">
        <!-- Row Loop -->
        <b-row ref="row">
          <b-col md="3">
            <form-select
              id="contact-type"
              v-model="form.type_id"
              v-if="isEdit === index"
              label="نوع وسيلة التواصل*"
              :disabled="isSaving || isDeleting"
              rules="required"
              ep="v2/types"
              :getOptionLabel="(option) => option.name_ar"
              :reduce="(option) => option.id"
            >
            </form-select>
            <template v-else>
              <div>نوع وسيلة الاتصال</div>
              <h6 class="my-1">{{ contact.type.name_ar }}</h6>
            </template>
          </b-col>
          <!-- Item Name -->
          <b-col md="4">
            <form-input
              label="القيمة*"
              :rules="
                form.type_id ? getValidationRules(form.type_id) : 'required'
              "
              :value="contact.value"
              v-model="form.value"
              id="contact-value"
              type="text"
              :disabled="isSaving || isDeleting"
              v-if="isEdit === index"
            />
            <template v-else>
              <div>القيمة</div>
              <h6 class="my-1">{{ contact.value }}</h6>
            </template>
          </b-col>

          <!-- Remove Button -->
          <b-col lg="4" md="3" class="mb-50">
            <template v-if="isEdit === index">
              <b-button
                @click="onUpdateSubmitted(index, contact)"
                variant="success"
                class="mt-0 mt-md-2"
                :disabled="isSaving || isDeleting"
              >
                <div v-if="isSaving" class="row align-items-center">
                  <b-spinner class="mx-50" small />
                  جار الحفظ ...
                </div>

                <div v-else>
                  <feather-icon icon="SaveIcon" class="mr-25" />
                  <span>حفظ</span>
                </div>
              </b-button>
              <b-button
                variant="flat-secondary"
                class="mt-0 mt-md-2 mx-50"
                @click="
                  isEdit = null;
                  form = {};
                "
                :disabled="isDeleting || isSaving"
              >
                إلغاء
              </b-button>
            </template>
            <div class="d-flex" v-else>
              <b-button
                v-can="{ slug: $route.meta.slug, action: 'U' }"
                @click="
                  isEdit = index;
                  form = { type_id: contact.type.id, value: contact.value };
                "
                variant="info"
                class="mt-0 mt-md-2"
              >
                <feather-icon icon="EditIcon" class="mr-25" />
                <span>تعديل</span>
              </b-button>
              <b-button
                variant="outline-danger"
                class="mt-0 mt-md-2 mx-50"
                @click="removeItem(index, contact)"
                :disabled="isDeleting || isSaving"
                v-can="{ slug: $route.meta.slug, action: 'D' }"
              >
                <div>
                  <feather-icon icon="XIcon" class="mr-25" />
                  <span>حذف</span>
                </div>
              </b-button>
            </div>
          </b-col>
          <b-col cols="12">
            <hr />
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
    <validation-observer
      v-if="isAddingNew"
      ref="newContactForm"
      #default="{ invalid }"
    >
      <b-form
        ref="addNewForm"
        :style="{ height: trHeight }"
        class="repeater-form"
        @submit.prevent="onNewSubmitted"
      >
        <!-- Row Loop -->
        <b-row ref="row">
          <b-col md="3">
            <form-select
              id="contact-type"
              v-model="newForm.type_id"
              label="نوع وسيلة التواصل*"
              :disabled="isSaving || isDeleting"
              rules="required"
              ep="v2/types"
              :getOptionLabel="(option) => option.name_ar"
              :reduce="(option) => option.id"
            >
            </form-select>
          </b-col>
          <b-col md="4">
            <form-input
              label="القيمة*"
              :rules="
                newForm.type_id
                  ? getValidationRules(newForm.type_id)
                  : 'required'
              "
              v-model="newForm.value"
              id="contact-value"
              type="text"
              :disabled="isSaving || isDeleting"
            />
          </b-col>

          <b-col lg="4" md="3" class="mb-50">
            <template>
              <b-button
                type="submit"
                variant="success"
                class="mt-0 mt-md-2"
                :disabled="isSaving || isDeleting"
              >
                <div v-if="isSaving" class="row align-items-center">
                  <b-spinner class="mx-50" small />
                  جار الحفظ ...
                </div>

                <div v-else>
                  <feather-icon icon="SaveIcon" class="mr-25" />
                  <span>حفظ</span>
                </div>
              </b-button>
              <b-button
                variant="flat-secondary"
                class="mt-0 mx-50 mt-md-2"
                @click="
                  isAddingNew = false;
                  newForm = {};
                "
                :disabled="isDeleting || isSaving"
              >
                إلغاء
              </b-button>
            </template>
          </b-col>
          <b-col cols="12">
            <hr />
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
    <b-button
      v-can="{ slug: $route.meta.slug, action: 'C' }"
      :disabled="isEdit !== null || isAddingNew !== false"
      variant="primary"
      @click="addNew"
    >
      <feather-icon icon="PlusIcon" class="mr-25" />
      <span>إضافة</span>
    </b-button>
  </app-collapse-item>
</template>

<script>
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";

import { required } from "@validations";

import {
  BRow,
  BCol,
  BContainer,
  BForm,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import { heightTransition } from "@core/mixins/ui/transition";
import { mapActions, mapGetters } from "vuex";
import formSelect from "@/components/form-select";
import formInput from "@/components/form-input";

export default {
  components: {
    BContainer,
    BSpinner,
    formInput,
    AppCollapseItem,
    BRow,
    formSelect,
    BButton,
    BCol,
    BForm,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [heightTransition],
  data() {
    return {
      isEdit: null,
      required,
      form: {
        value: "",
        type_id: null,
      },
      newForm: {},
      isAddingNew: false,
      validationsMap: [
        {
          id: 1,
          name_ar: "فيسبوك",

          rules: "required|url",
        },
        {
          id: 2,
          name_ar: "واتساب",

          rules: "required|digits:10|min:10|max:10",
        },
        {
          id: 3,
          name_ar: "البريد الالكتروني",

          rules: "required|email",
        },
        {
          id: 4,
          name_ar: "سكايب",

          rules: "required|url",
        },
        {
          id: 5,
          name_ar: "إنستغرام",

          rules: "required|url",
        },
        {
          id: 6,
          name_ar: "رقم الهاتف",

          rules: "required|integer|min:4|max:10",
        },
        {
          id: 7,
          name_ar: "فاكس",

          rules: "required|digits:10|min:10|max:10",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("NgoProfile/ContactInfo", ["isSaving", "isDeleting"]),
    isOneEdit() {
      return this.isEdit !== null;
    },
  },
  methods: {
    ...mapActions("NgoProfile/ContactInfo", [
      "saveContact",
      "updateContact",
      "deleteContact",
    ]),
    removeItem(index, contact) {
      this.$bvModal
        .msgBoxConfirm("هل أنت متأكد من حذف الاجتماع", {
          title: "تأكيد حذف الاجتماع",
          size: "sm",
          okVariant: "danger",
          okTitle: "تأكيد",
          cancelTitle: "إلغاء",
          cancelVariant: "outline-secondary",
          hideHeaderClose: false,
          centered: true,
        })
        .then((value) => {
          if (value) {
            if (contact.id) {
              this.deleteContact({
                id: contact.id,
                branch_id: this.branchInfo.id,
              }).then(() => {
                this.isEdit = null;
                this.trTrimHeight(this.$refs.row[0].offsetHeight);
              });
            } else {
              this.contactMethods.splice(index, 1);
            }
          }
        });
    },
    getValidationRules(contact_id) {
      return this.validationsMap.find((e) => e.id === contact_id).rules;
    },
    addNew() {
      this.newForm = {};
      this.isAddingNew = true;
    },
    onNewSubmitted() {
      this.$refs.newContactForm.validate().then((success) => {
        if (success) {
          if (!this.isNgo) {
            this.newForm.branch_id = this.branchInfo.id;
          }
          this.saveContact(this.newForm).then(() => {
            this.newForm = {};
            this.isAddingNew = false;
          });
        }
      });
    },
    onUpdateSubmitted(index, contact) {
      if (!this.isNgo) {
        this.form.branch_id = this.branchInfo.id;
      }
      this.$refs.updateContactForm[index].validate().then((success) => {
        if (success) {
          this.updateContact({ form: this.form, id: contact.id }).then(() => {
            this.form = {
              value: "",
              type_id: null,
            };
            this.isEdit = null;
          });
        }
      });
    },
    initTrHeight() {
      this.trSetHeight(null);
      this.$nextTick(() => {
        this.trSetHeight(this.$refs.form.scrollHeight);
      });
    },
  },
  mounted() {
    this.initTrHeight();
  },
  created() {
    window.addEventListener("resize", this.initTrHeight);
  },
  destroyed() {
    window.removeEventListener("resize", this.initTrHeight);
  },
  props: {
    title: String,
    contactMethods: {
      type: Array,
      default: () => [],
    },
    isNgo: Boolean,
    branchInfo: Object,
  },
};
</script>
